// Client object store: server-confirmed objects plus an optimistic layer of pending transactions, with indexes by type and by link.

import {
  applyPatch, MetaIndex, META_KINDS, type AnyObj, type Change, type FieldDef, type Id, type LinkObj, type MetaObj,
  type QueryContext, type RecordObj,
} from '@confdeck/core-shared';

/** One object operation a pending transaction predicts; the same shape as a journal change, without a sync id. */
export interface PendingOp { kind: AnyObj['kind']; id: Id; op: 'put' | 'patch' | 'delete'; data: Record<string, unknown> | null }

export interface PendingTx {
  clientTxId: string;
  command: string;
  input: unknown;
  ops: PendingOp[];
  /** Why the person made the change, when a stage or the setting asks for it (R-C14, R-C15). */
  reason?: string;
  /** Sync id from the server ack; the tx leaves the overlay once the store has caught up to it. */
  ackSyncId?: number;
}

export type StoreEvent =
  | { scope: 'meta' }
  | { scope: 'records'; typeId: Id; ids: Id[] }
  | { scope: 'links'; recordIds: Id[] }
  | { scope: 'status' };

const isMeta = (k: string): boolean => (META_KINDS as readonly string[]).includes(k);

/** Link index: record → relation → other record → number of links (client and server copies may overlap for a moment). */
type LinkIndex = Map<Id, Map<Id, Map<Id, number>>>;

function track(ix: Map<Id, Set<Id>>, l: LinkObj, add: boolean) {
  for (const rid of [l.fromId, l.toId]) {
    let set = ix.get(rid);
    if (!set) ix.set(rid, (set = new Set()));
    if (add) set.add(l.id); else set.delete(l.id);
  }
}

function bump(ix: LinkIndex, a: Id, rel: Id, b: Id, d: number) {
  let byRel = ix.get(a);
  if (!byRel) ix.set(a, (byRel = new Map()));
  let others = byRel.get(rel);
  if (!others) byRel.set(rel, (others = new Map()));
  const n = (others.get(b) ?? 0) + d;
  if (n <= 0) others.delete(b); else others.set(b, n);
}

export class Store implements QueryContext {
  syncId = 0;
  meta = new MetaIndex();
  /** Server-confirmed objects. */
  private base = new Map<Id, AnyObj>();
  /** Visible objects that differ from base because of pending transactions (undefined value = hidden). */
  private overlay = new Map<Id, AnyObj | null>();
  readonly pending: PendingTx[] = [];
  private byType = new Map<Id, Set<Id>>();
  private linksFrom: LinkIndex = new Map();
  private linksTo: LinkIndex = new Map();
  /** Record → ids of live link objects touching it, to find the exact links a relation edit removes. */
  private linkIds = new Map<Id, Set<Id>>();
  private listeners = new Set<(e: StoreEvent[]) => void>();
  private queued: StoreEvent[] = [];
  private flushScheduled = false;
  /** Bumped per type on any record change; queries memoize on it. */
  readonly typeVersion = new Map<Id, number>();
  metaVersion = 0;
  /** Ids touched since the last persist, for the IndexedDB writer. */
  readonly dirty = new Set<Id>();
  readonly removed = new Set<Id>();

  // --- reading -------------------------------------------------------------------------------

  get(id: Id): AnyObj | undefined {
    if (this.overlay.has(id)) return this.overlay.get(id) ?? undefined;
    return this.base.get(id);
  }
  record(id: Id): RecordObj | undefined {
    const o = this.get(id);
    return o?.kind === 'record' && !o.archivedAt ? o : undefined;
  }
  field(typeId: Id, key: string): FieldDef | undefined { return this.meta.field(typeId, key); }
  subtree(termId: Id): Set<Id> { return this.meta.subtree(termId); }
  targetType(field: FieldDef): Id | undefined { return this.meta.relationTarget(field)?.typeId; }

  recordIds(typeId: Id): Set<Id> { return this.byType.get(typeId) ?? new Set(); }
  count(typeId: Id): number { return this.byType.get(typeId)?.size ?? 0; }

  /** Ids linked through a relation field, in no particular order. */
  linked(rec: RecordObj, field: FieldDef): Id[] {
    const relId = field.options.relationId;
    if (!relId) return [];
    const ix = field.options.side === 'to' ? this.linksTo : this.linksFrom;
    const m = ix.get(rec.id)?.get(relId);
    return m ? [...m.keys()] : [];
  }

  reachedBy(field: FieldDef, test: (target: RecordObj) => boolean): Set<Id> {
    const out = new Set<Id>();
    const target = this.meta.relationTarget(field);
    const relId = field.options.relationId;
    if (!target || !relId) return out;
    // The reverse side: from a target record back to the records whose field points at it.
    const back = field.options.side === 'to' ? this.linksFrom : this.linksTo;
    for (const id of this.recordIds(target.typeId)) {
      const t = this.record(id);
      if (!t || !test(t)) continue;
      const m = back.get(id)?.get(relId);
      if (m) for (const src of m.keys()) out.add(src);
    }
    return out;
  }

  anyLinked(rec: RecordObj, field: FieldDef, test: (id: Id) => boolean): boolean {
    const relId = field.options.relationId;
    if (!relId) return false;
    const m = (field.options.side === 'to' ? this.linksTo : this.linksFrom).get(rec.id)?.get(relId);
    if (m) for (const id of m.keys()) if (test(id)) return true;
    return false;
  }

  /** Every link object (confirmed or pending) between two records of a relation. */
  linkObjects(relationId: Id, recordId: Id, side: 'from' | 'to'): LinkObj[] {
    const out: LinkObj[] = [];
    for (const lid of this.linkIds.get(recordId) ?? []) {
      const o = this.get(lid);
      if (o?.kind === 'link' && !o.archivedAt && o.relationId === relationId && (side === 'from' ? o.fromId : o.toId) === recordId) out.push(o);
    }
    return out;
  }

  *iterate(): Generator<AnyObj> {
    for (const [id, o] of this.base) if (!this.overlay.has(id)) yield o;
    for (const o of this.overlay.values()) if (o) yield o;
  }

  // --- subscriptions -------------------------------------------------------------------------

  subscribe(fn: (e: StoreEvent[]) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: StoreEvent) {
    if (e.scope === 'records') this.typeVersion.set(e.typeId, (this.typeVersion.get(e.typeId) ?? 0) + 1);
    if (e.scope === 'meta') this.metaVersion++;
    this.queued.push(e);
    if (!this.flushScheduled) {
      this.flushScheduled = true;
      // One notification per microtask: a batch of 1000 changes re-renders once.
      queueMicrotask(() => {
        this.flushScheduled = false;
        const batch = this.queued;
        this.queued = [];
        for (const fn of this.listeners) fn(batch);
      });
    }
  }
  status() { this.emit({ scope: 'status' }); }

  // --- index maintenance ---------------------------------------------------------------------

  /** Moves an id from its previous visible state to the new one, updating every index. */
  private reindex(id: Id, prev: AnyObj | undefined, next: AnyObj | undefined) {
    const prevLive = prev && !prev.archivedAt ? prev : undefined;
    const nextLive = next && !next.archivedAt ? next : undefined;
    const kind = (next ?? prev)?.kind;
    if (!kind) return;
    if (kind === 'record') {
      const p = prevLive as RecordObj | undefined, n = nextLive as RecordObj | undefined;
      if (p && (!n || p.typeId !== n.typeId)) this.byType.get(p.typeId)?.delete(id);
      if (n) { let s = this.byType.get(n.typeId); if (!s) this.byType.set(n.typeId, (s = new Set())); s.add(id); }
      const typeId = (n ?? p ?? (next as RecordObj) ?? (prev as RecordObj)).typeId;
      this.emit({ scope: 'records', typeId, ids: [id] });
    } else if (kind === 'link') {
      const p = prevLive as LinkObj | undefined, n = nextLive as LinkObj | undefined;
      if (p) { bump(this.linksFrom, p.fromId, p.relationId, p.toId, -1); bump(this.linksTo, p.toId, p.relationId, p.fromId, -1); track(this.linkIds, p, false); }
      if (n) { bump(this.linksFrom, n.fromId, n.relationId, n.toId, 1); bump(this.linksTo, n.toId, n.relationId, n.fromId, 1); track(this.linkIds, n, true); }
      const ids = [...new Set([p?.fromId, p?.toId, n?.fromId, n?.toId].filter(Boolean) as Id[])];
      this.emit({ scope: 'links', recordIds: ids });
      // A link change can change how linked records filter and sort; notify their types too.
      for (const rid of ids) { const r = this.record(rid); if (r) this.emit({ scope: 'records', typeId: r.typeId, ids: [rid] }); }
    } else if (isMeta(kind)) {
      if (next) this.meta.put(next as MetaObj); else this.meta.delete(id);
      this.emit({ scope: 'meta' });
    }
  }

  /** Recomputes the visible object of `id` from base plus pending ops, in transaction order. */
  private recompute(id: Id, prevVisible?: AnyObj | undefined, hasPrev = false) {
    // The caller may already have changed base; then it passes what was visible before the change.
    const prev = hasPrev ? prevVisible : this.get(id);
    let obj: AnyObj | undefined = this.base.get(id);
    let touched = false;
    for (const tx of this.pending) for (const op of tx.ops) {
      if (op.id !== id) continue;
      touched = true;
      if (op.op === 'put') obj = { ...(op.data as unknown as AnyObj), rev: obj?.rev ?? 0 };
      else if (op.op === 'patch' && obj) obj = applyPatch(obj, op.data ?? {}, obj.rev);
      else if (op.op === 'delete') obj = undefined;
    }
    if (touched) this.overlay.set(id, obj ?? null); else this.overlay.delete(id);
    const next = this.get(id);
    if (prev !== next) this.reindex(id, prev, next);
  }

  // --- writing -------------------------------------------------------------------------------

  /** Loads snapshot objects without notifications per object; call `loaded()` after the batch. */
  loadBase(objs: AnyObj[]) {
    for (const o of objs) {
      const prev = this.get(o.id);
      this.base.set(o.id, o);
      if (!this.overlay.has(o.id)) this.reindexQuiet(o.id, prev, o);
    }
  }

  private reindexQuiet(id: Id, prev: AnyObj | undefined, next: AnyObj) {
    if (next.kind === 'record') {
      if (prev?.kind === 'record' && prev.typeId !== next.typeId) this.byType.get(prev.typeId)?.delete(id);
      if (!next.archivedAt) { let s = this.byType.get(next.typeId); if (!s) this.byType.set(next.typeId, (s = new Set())); s.add(id); }
      else this.byType.get(next.typeId)?.delete(id);
    } else if (next.kind === 'link') {
      if (prev?.kind === 'link' && !prev.archivedAt) { bump(this.linksFrom, prev.fromId, prev.relationId, prev.toId, -1); bump(this.linksTo, prev.toId, prev.relationId, prev.fromId, -1); track(this.linkIds, prev, false); }
      if (!next.archivedAt) { bump(this.linksFrom, next.fromId, next.relationId, next.toId, 1); bump(this.linksTo, next.toId, next.relationId, next.fromId, 1); track(this.linkIds, next, true); }
    } else this.meta.put(next as MetaObj);
  }

  /** Records of a type arrived in bulk: queries over it recompute once. */
  touchType(typeId: Id) { this.emit({ scope: 'records', typeId, ids: [] }); }

  /** After a bulk load: one notification for everything. */
  loaded() {
    for (const typeId of this.byType.keys()) this.emit({ scope: 'records', typeId, ids: [] });
    this.emit({ scope: 'meta' });
  }

  reset() {
    this.base.clear(); this.overlay.clear(); this.byType.clear(); this.linksFrom.clear(); this.linksTo.clear(); this.linkIds.clear();
    this.meta = new MetaIndex(); this.syncId = 0; this.dirty.clear(); this.removed.clear();
  }

  /** Applies confirmed changes from the server in order. */
  applyChanges(changes: Change[]) {
    for (const c of changes) {
      // Equal sync ids are applied: the server sends a moved record and a removal along with the batch under its last id.
      if (c.syncId < this.syncId) continue;
      const before = this.get(c.id);
      const cur = this.base.get(c.id);
      if (c.op === 'put') this.base.set(c.id, { ...(c.data as unknown as AnyObj), rev: c.syncId });
      else if (c.op === 'patch' && cur) this.base.set(c.id, applyPatch(cur, c.data ?? {}, c.syncId));
      else if (c.op === 'delete') this.base.delete(c.id);
      this.syncId = c.syncId;
      if (this.base.has(c.id)) this.dirty.add(c.id); else this.removed.add(c.id);
      this.recompute(c.id, before, true);
    }
  }

  /** Adds an optimistic transaction: its ops are visible immediately. */
  addPending(tx: PendingTx) {
    // The outbox read back at the end of a load may hold an edit made while the load was still running: it is pending already.
    if (this.pending.some((p) => p.clientTxId === tx.clientTxId)) return;
    this.pending.push(tx);
    for (const id of new Set(tx.ops.map((o) => o.id))) this.recompute(id);
    this.status();
  }

  /**
   * The server confirmed the transaction. It acks only after sending this client the transaction's changes, so the
   * optimistic copy goes now: whatever the client may see of the result is already in `base`, and a change this
   * person may not see (moved out of their event) never comes, so waiting for it would leave a ghost forever.
   */
  ack(clientTxId: string, syncId: number) {
    const i = this.pending.findIndex((t) => t.clientTxId === clientTxId);
    if (i < 0) return;
    const [tx] = this.pending.splice(i, 1);
    tx!.ackSyncId = syncId;
    for (const id of new Set(tx!.ops.map((o) => o.id))) this.recompute(id);
    this.status();
  }

  /** Removes a rejected transaction; the visible state rolls back to base plus the remaining pending ops. */
  reject(clientTxId: string): PendingTx | undefined {
    const i = this.pending.findIndex((t) => t.clientTxId === clientTxId);
    if (i < 0) return undefined;
    const [tx] = this.pending.splice(i, 1);
    for (const id of new Set(tx!.ops.map((o) => o.id))) this.recompute(id);
    this.status();
    return tx;
  }

  /** Everything confirmed, for persistence. */
  confirmed(): IterableIterator<AnyObj> { return this.base.values(); }
  confirmedGet(id: Id): AnyObj | undefined { return this.base.get(id); }
}
